import React, {
  type ComponentType,
  Fragment,
  useContext,
  useEffect,
  useState,
} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import cs from 'classnames';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {
  convertLocationParamsToFetchParams,
  useAddToFolder,
  useDocuments,
} from 'app/documents/api';
import {useDeleteFolder, useFolders} from 'app/folders/api';
import {DocumentItem} from 'app/folders/components/DocumentItem';
import {useMoveModal} from 'app/folders/components/MoveFolderModal';
import {ItemRelationsListOfGenerics} from 'app/general/dashboards/ItemRelationsListOfGenerics';
import ThreeDotsDropdownMenu from 'app/general/widgets/ThreeDotsDropdownMenu';
import {KBTreeCreateArticleItem} from 'app/kbs/components/KBEditPage/KBTree/KBTreeArticleItem';
import {useDeleteModal} from 'app/kbs/components/KBEditPage/KBTree/KBTreeDeleteItemModal';
import {KBEditItemContext} from 'app/kbs/components/KBEditPage/KBTree/KBTreeEditItemModal';
import {KBTreeCollapsibleItemStructure} from 'app/kbs/components/KBEditPage/KBTree/KBTreeStructures';
import {gt} from 'app/utils/i18n';

import {CreateFolderButton} from './dashboards/CreateFolder';
import {CreateFolderLink} from './dashboards/CreateFolderLink';

import {isAncestor} from '../utils';

import type {MenuItemsType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {
  BaseDocumentContentType,
  BasicTeamSerializedType,
  FolderRecurrentParentSerializedType,
  FolderSerializedType,
  KBCategorySerializedType,
  KnowledgebaseSerializedType,
  SearchSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type GenericDocumentItemSerializedType =
  | FolderSerializedType
  | KBCategorySerializedType
  | KnowledgebaseSerializedType;

export type FolderItemProps = {
  folder: GenericDocumentItemSerializedType;
  afterAction?: () => Promise<any>;
  isParentDirty: boolean;
  isMillerColumn?: boolean;
  parent?: GenericDocumentItemSerializedType;
  setParentFolder?: (GenericDocumentItemSerializedType) => void;
  actionsGetter?: (
    object: any,
    afterAction?: () => Promise<any>
  ) => MenuItemsType;
  team?: TeamSerializedType;
  isHidingDocuments?: boolean;
  isHidingTeams?: boolean;
  isHidingCreateDocument?: boolean;
  isHidingCreateFolder?: boolean;
  isCreateFolderPopup?: boolean;
  onToggle?: (
    isExpanded: boolean,
    folder: GenericDocumentItemSerializedType,
    promise: Promise<any>
  ) => any;
  selectedHashid?: string;
  itemCaption?: string;
  selectedFolder?: FolderRecurrentParentSerializedType;
  defaultFolderIcon?: string;
  tooltipText?: string;
  FolderItemReplacement?: ComponentType<any>;
  initiallyExpanded?: boolean;
  small?: boolean;
  itemProps?: {
    callback: (...args: any) => void;
    selectedItems: Array<GenericDocumentItemSerializedType>;
    checkboxEnabled: boolean;
    allowFolderCreation: boolean;
    initialExpandedFolders?: Array<string>;
  };
};

export const FolderItem = (props: FolderItemProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [deleteFolder] = useDeleteFolder();
  const [addToFolder] = useAddToFolder();
  const {show: showMoveModal, Modal} = useMoveModal();
  const setDeleting = useDeleteModal();
  const {setEditingProps: setEditing, editingProps} =
    useContext(KBEditItemContext);
  const [isDirty, setIsDirty] = useState<boolean>(props.isParentDirty);

  const folderItem =
    'content_type' in props.folder && props.folder.content_type == 'folder'
      ? props.folder
      : null;

  const {data: documents, refetch: refetchDocuments} = useDocuments(
    props.isHidingDocuments || folderItem == null
      ? skipToken
      : convertLocationParamsToFetchParams({folder: folderItem.hashid})
  );
  const {
    data: folders,
    refetch: refetchFolders,
    isFetching: isFetchingFolders,
  } = useFolders(
    {
      parent: folderItem?.hashid,
      team_id: props.team?.id,
    },
    {skip: !expanded}
  );
  let teams: Array<BasicTeamSerializedType> = [];
  let isSelected = false;
  let canEdit = false;

  useEffect(() => {
    if (
      editingProps != null &&
      props.folder.content_type == 'folder' &&
      editingProps.item.hashid === props.folder.hashid
    ) {
      setEditing({
        item: props.folder,
        parent: props.parent,
        onClose: () => null,
      });
    }
  }, [props.folder]);

  useEffect(() => {
    setExpanded(
      isSelected ||
        (props.selectedFolder != null &&
          isAncestor(props.folder, props.selectedFolder))
    );
  }, [
    props.selectedHashid,
    'hashid' in props.folder && props.folder?.hashid,
    props.selectedFolder,
  ]);

  if ('teams' in props.folder) {
    teams = props.folder.teams;
  }

  if ('hashid' in props.folder) {
    isSelected = props.folder.hashid === props.selectedHashid;
  }

  if ('can_edit' in props.folder) {
    canEdit = props.folder.can_edit;
  }

  const folderIcon =
    'icon' in props.folder && props.folder.icon
      ? props.folder.icon
      : props.defaultFolderIcon
      ? props.defaultFolderIcon
      : null;

  function doCreateDocument(
    value: SearchSerializedType<BaseDocumentContentType>
  ) {
    if (folderItem) {
      const payload = {
        document_content_type: value.content_type,
        object_id: value.object_id,
        folder: folderItem.url,
      };
      return addToFolder(payload);
    }
    return Promise.resolve(true);
  }

  const loadDocs = (): Promise<any> => {
    refetchDocuments();
    return Promise.resolve(true);
  };

  function loadFolders() {
    refetchFolders();
    return Promise.resolve(true);
  }

  useEffect(() => {
    if (props.isParentDirty) {
      setIsDirty(true);
    }
  }, [props.isParentDirty]);

  useEffect(() => {
    if (isDirty) {
      refetchFolders();
      setIsDirty(false);
    }
  }, [isDirty]);

  useEffect(() => {
    if (props.initiallyExpanded && !expanded) {
      toggleFolder();
    }
  }, [props.initiallyExpanded]);

  const toggleFolder = () => {
    let promise;
    if (!expanded && folderItem) {
      const p1 = loadFolders();
      const p2 = loadDocs();
      promise = Promise.all([p1, p2]);
    } else {
      promise = Promise.resolve(true);
    }
    setExpanded(!expanded);
    if (props.onToggle != null) {
      props.onToggle(!expanded, props.folder, promise);
    }
    if (props.isMillerColumn && props?.setParentFolder) {
      props?.setParentFolder(props.folder);
    }
  };

  function actionsGetter(): MenuItemsType {
    return [
      {
        text: gt('Edit'),
        key: 'edit-folder',
        icon: 'fas fa-pen',
        condition: () => canEdit,
        callback: () =>
          props.folder.content_type == 'folder'
            ? setEditing({
                item: props.folder,
                parent: props.parent,
                onClose: () => null,
              })
            : undefined,
      },
      {
        text: gt('Move'),
        icon: 'fa fa-arrows-alt',
        key: 'move-folder',
        condition: () => canEdit && props.folder.content_type == 'folder',
        callback: () => {
          props.folder.content_type == 'folder'
            ? showMoveModal({
                object: props.folder,
                action: () => Promise.resolve(true),
                callback: props.afterAction,
                help: gt(
                  'When you move this folder, all its contents will move with it. It will adopt the teams of the folder you place it into.'
                ),
              })
            : undefined;
        },
      },
      {
        text: gt('Delete'),
        icon: 'far fa-trash',
        key: 'remove-folder',
        condition: () => canEdit,
        callback: () =>
          setDeleting({
            object: props.folder,
            action: deleteFolder,
            callback: props.afterAction,
            help: gt(
              'This folder and all sub folders and documents will be deleted.'
            ),
          }),
      },
    ];
  }

  const actionsGetterWrapper = (folder: FolderSerializedType) =>
    props.actionsGetter != null
      ? props.actionsGetter(folder, props.afterAction)
      : [];

  if (
    props.selectedFolder != null &&
    !isFetchingFolders &&
    !expanded &&
    (isSelected || isAncestor(props.folder, props.selectedFolder))
  ) {
    setExpanded(true);
    Promise.all([loadFolders(), loadDocs()]);
  }

  const Folder = props.FolderItemReplacement || FolderItem;

  return (
    <Fragment>
      <KBTreeCollapsibleItemStructure
        active={isSelected}
        isMillerColumn={props.isMillerColumn}
        small={props.small}
        icon={
          folderIcon
            ? folderIcon
            : expanded
            ? 'far fa-folder-open'
            : 'far fa-folder'
        }
        title={
          <LazyTooltip
            tooltip={props.tooltipText}
            hide={props.tooltipText == null || isSelected}
          >
            <div>
              <span className="d-block">{props.folder.name}</span>
              {props.itemCaption && (
                <small className="block">{props.itemCaption}</small>
              )}
            </div>
          </LazyTooltip>
        }
        additionalContent={
          props.isHidingTeams ? null : (
            <ItemRelationsListOfGenerics
              items={teams.map((team) => ({
                name: team.name,
                html_url: team.html_url,
              }))}
              contentType={'team'}
            />
          )
        }
        onToggleExpand={toggleFolder}
        expanded={expanded}
        loading={expanded && isFetchingFolders}
        actionsChildren={
          <ThreeDotsDropdownMenu
            className={cs({
              'btn btn-sm btn-link py-0 px-1': props.small,
              'text-light': isSelected,
            })}
            object={props.folder}
            actionsGetter={
              props.actionsGetter ? actionsGetterWrapper : actionsGetter
            }
          />
        }
      >
        {folders?.results.map((folder) => (
          <Folder
            small={props.small}
            key={folder.hashid}
            folder={folder}
            selectedHashid={props.selectedHashid}
            selectedFolder={props.selectedFolder}
            tooltipText={props.tooltipText}
            afterAction={loadFolders}
            parent={props.folder}
            isParentDirty={isDirty}
            team={props.team}
            actionsGetter={props.actionsGetter}
            isHidingCreateDocument={props.isHidingCreateDocument}
            isHidingCreateFolder={props.isHidingCreateFolder}
            isCreateFolderPopup={props.isCreateFolderPopup}
            isHidingDocuments={props.isHidingDocuments}
            isHidingTeams={props.isHidingTeams}
            onToggle={props.onToggle}
            FolderItemReplacement={props.FolderItemReplacement}
            itemProps={props.itemProps}
          />
        ))}

        {documents?.results.map((document) => (
          <DocumentItem
            key={document.url}
            document={document}
            afterAction={loadDocs}
          />
        ))}

        {props.isHidingCreateDocument || !canEdit ? null : folderItem ? (
          <KBTreeCreateArticleItem
            create={doCreateDocument}
            contentTypes={['procedure', 'policy', 'proceduregroup']}
            buttonDescription={gt('Add new Document')}
            placeholderText={gt(
              'Select a procedure, process or policy to add...'
            )}
            excludeParameter={{exclude_folder_id: folderItem.id}}
            afterAction={loadDocs}
          />
        ) : null}

        {!props.isHidingCreateFolder && canEdit ? (
          !props.isCreateFolderPopup ? (
            <CreateFolderButton
              canEdit={canEdit}
              allowedTeams={teams}
              parent={folderItem}
            />
          ) : (
            <CreateFolderLink
              autoFocus={true}
              parent={folderItem}
              teams={teams}
              canEdit={canEdit}
            />
          )
        ) : null}
      </KBTreeCollapsibleItemStructure>

      {Modal}
    </Fragment>
  );
};
